/**
 * RedPlateExpiryAlert — Jun-2026 (User-Wunsch).
 *
 * Dezenter, aber sichtbarer Hinweis-Banner für ablaufende/abgelaufene „Rote Hefte"
 * (Zulassung der Probefahrtkennzeichen). Lädt `/api/dealer/red-plates/expiring`
 * und zeigt nur dann etwas an, wenn mindestens ein Kennzeichen in <= 4 Wochen
 * abläuft oder bereits abgelaufen ist. So geht keine ablaufende Zulassung unter,
 * selbst wenn niemand die Erinnerungs-Mails liest. Verlinkt auf die Verwaltung.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookText, AlertTriangle, ChevronRight } from "lucide-react";
import api from "../../lib/api";

export default function RedPlateExpiryAlert({ className = "" }) {
    const [items, setItems] = useState([]);

    useEffect(() => {
        let cancel = false;
        api.get("/dealer/red-plates/expiring")
            .then(({ data }) => { if (!cancel) setItems(data.items || []); })
            .catch(() => { /* still: nur Hinweis, keine harte Abhängigkeit */ });
        return () => { cancel = true; };
    }, []);

    if (!items.length) return null;
    const expired = items.filter((p) => p.expired);

    return (
        <Link
            to="/dealer/rote-nummer"
            className={`flex items-center gap-3 rounded-sm border px-4 py-3 transition-colors group ${expired.length
                ? "bg-red-50 border-red-300 hover:bg-red-100 text-red-800"
                : "bg-amber-50 border-amber-300 hover:bg-amber-100 text-amber-800"} ${className}`}
            data-testid="red-plate-expiry-alert"
        >
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="text-sm font-bold flex items-center gap-1.5">
                    <BookText className="w-4 h-4" /> Rotes Heft beachten
                </div>
                <div className="text-xs mt-0.5">
                    {expired.length > 0 && (
                        <span className="font-bold" data-testid="red-plate-expiry-alert-expired">{expired.length} abgelaufen</span>
                    )}
                    {expired.length > 0 && items.length - expired.length > 0 && <span> · </span>}
                    {items.length - expired.length > 0 && (
                        <span data-testid="red-plate-expiry-alert-soon">{items.length - expired.length} läuft in ≤ 4 Wochen ab</span>
                    )}
                    {": "}
                    {items.slice(0, 3).map((p) => p.plate).join(", ")}{items.length > 3 ? " …" : ""}
                </div>
            </div>
            <ChevronRight className="w-4 h-4 shrink-0 opacity-60 group-hover:translate-x-0.5 transition-transform" />
        </Link>
    );
}
